import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { css } from "dx-styles";

import { button, errorText, muted, row, stack } from "../components/primitives";
import type { BundleItem, Parameter, TemplateRule } from "../lib/document";
import { ruleMatchesText, ruleProblem, ruleSummary } from "../lib/ruleSummary";
import { tokens } from "../theme/tokens";

/** Two levels only: the engine recurses further, deeper than two is undebuggable. */
export const maxConditionalDepth = 2;

const item = css({
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: tokens.radius.sm,
  backgroundColor: tokens.color.paper,
  padding: tokens.space.xs,
  display: "flex",
  alignItems: "flex-start",
  gap: tokens.space.xs,
});

const itemSelected = css({ borderColor: tokens.color.olive, backgroundColor: tokens.color.kraft });

const grip = css({
  cursor: "grab",
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: tokens.radius.sm,
  background: "none",
  padding: `0 ${tokens.space.xs}`,
  minHeight: "26px",
  // Focusable and operable from the keyboard: dnd-kit's KeyboardSensor takes
  // over from here (space to lift, arrows to move, space to drop).
  "&:focus-visible": { borderColor: tokens.color.signal },
});

const body = css({ flex: 1, textAlign: "start", background: "none", border: "none", cursor: "pointer", padding: 0 });

const nested = css({ marginInlineStart: tokens.space.md, marginTop: tokens.space.xs });

interface Props {
  readonly rules: readonly TemplateRule[];
  readonly items: readonly BundleItem[];
  readonly parameters: readonly Parameter[];
  readonly selectedRuleId: string | null;
  /** Rules the engine reported a message for, from the last preview. */
  readonly issueRuleIds?: readonly string[];
  readonly depth?: number;
  readonly onSelect: (ruleId: string) => void;
  readonly onReorder: (rules: readonly TemplateRule[]) => void;
  readonly onChangeRule: (rule: TemplateRule) => void;
}

/**
 * The rule list, reorderable with a pointer or the keyboard.
 *
 * Order is semantic here — rules apply in sequence, and an `exclude` after an
 * `include` is not the same collection as the other way round — so reordering is
 * a first-class edit rather than a convenience.
 */
export function RuleTree({
  rules,
  items,
  parameters,
  selectedRuleId,
  issueRuleIds = [],
  depth = 1,
  onSelect,
  onReorder,
  onChangeRule,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = rules.findIndex((rule) => rule.id === active.id);
    const to = rules.findIndex((rule) => rule.id === over.id);
    if (from < 0 || to < 0) return;
    const next = [...rules];
    const [moved] = next.splice(from, 1);
    if (!moved) return;
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={rules.map((rule) => rule.id)} strategy={verticalListSortingStrategy}>
        <div className={stack}>
          {rules.map((rule) => (
            <SortableRule
              key={rule.id}
              rule={rule}
              items={items}
              parameters={parameters}
              selectedRuleId={selectedRuleId}
              issueRuleIds={issueRuleIds}
              depth={depth}
              onSelect={onSelect}
              onChangeRule={onChangeRule}
              onReorderNested={(nestedRules) => onChangeRule({ ...rule, conditionalRules: nestedRules })}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableRule({
  rule,
  items,
  parameters,
  selectedRuleId,
  issueRuleIds,
  depth,
  onSelect,
  onChangeRule,
  onReorderNested,
}: {
  readonly rule: TemplateRule;
  readonly items: readonly BundleItem[];
  readonly parameters: readonly Parameter[];
  readonly selectedRuleId: string | null;
  readonly issueRuleIds: readonly string[];
  readonly depth: number;
  readonly onSelect: (ruleId: string) => void;
  readonly onChangeRule: (rule: TemplateRule) => void;
  readonly onReorderNested: (rules: readonly TemplateRule[]) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id });
  const problem = ruleProblem(rule, parameters);
  const matches = ruleMatchesText(rule, items);
  const engineFlagged = issueRuleIds.some((id) => id.toLowerCase() === rule.id.toLowerCase());

  return (
    <div>
      <div
        ref={setNodeRef}
        className={rule.id === selectedRuleId ? `${item} ${itemSelected}` : item}
        // Transform values change per drag frame, so they belong in the style
        // attribute rather than in a static css() rule.
        style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 }}
      >
        <button
          type="button"
          className={grip}
          aria-label={`Reorder ${ruleSummary(rule, items)}`}
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </button>
        <button type="button" className={body} onClick={() => onSelect(rule.id)}>
          <div className={stack}>
            <span>{ruleSummary(rule, items)}</span>
            {matches ? <span className={muted}>{matches}</span> : null}
            {problem ? <span className={errorText}>{problem}</span> : null}
            {!problem && engineFlagged ? (
              <span className={muted}>The engine had something to say about this rule — see the preview.</span>
            ) : null}
          </div>
        </button>
      </div>

      {rule.type === "conditional" ? (
        <div className={nested}>
          <RuleTree
            rules={rule.conditionalRules ?? []}
            items={items}
            parameters={parameters}
            selectedRuleId={selectedRuleId}
            issueRuleIds={issueRuleIds}
            depth={depth + 1}
            onSelect={onSelect}
            onReorder={onReorderNested}
            onChangeRule={(nestedRule) =>
              onChangeRule({
                ...rule,
                conditionalRules: (rule.conditionalRules ?? []).map((candidate) =>
                  candidate.id === nestedRule.id ? nestedRule : candidate,
                ),
              })
            }
          />
          {depth >= maxConditionalDepth ? (
            <span className={muted}>
              Nesting stops here. The engine goes deeper, but a rule three conditions down cannot be reasoned
              about.
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function RuleTreeHint() {
  return (
    <span className={muted}>
      Rules apply in order. Drag the handle, or focus it and press space, then the arrow keys.
    </span>
  );
}

export function AddRuleMenu({ onAdd }: { readonly onAdd: (type: TemplateRule["type"]) => void }) {
  const types: readonly TemplateRule["type"][] = [
    "include",
    "exclude",
    "includeQuick",
    "override",
    "conditional",
    "includeGroup",
    "excludeGroup",
  ];
  return (
    <div className={row} style={{ flexWrap: "wrap" }}>
      {types.map((type) => (
        <button key={type} type="button" className={button({ size: "sm" })} onClick={() => onAdd(type)}>
          + {type}
        </button>
      ))}
    </div>
  );
}
